export const COLORS = {
  bg: '#bebbb3',
  bgDark: '#141c2c',
  bgWhite: '#ffffff',
  text: '#3a3a3a',
  textMuted: '#909090',
  /** Gray that still clears 4.5:1 on white and on the beige page background. */
  textMutedOnLight: '#454545',
  textOnDark: '#ffffff',
  textOnDarkMuted: 'rgba(255,255,255,0.8)',
  textEmphasis: 'rgba(255,255,255,0.88)',
  inputPlaceholder: 'rgba(255,255,255,0.7)',
  btnBorderLight: 'rgba(93,93,93,0.15)',
  btnBorderDark: 'rgba(255,255,255,0.3)',
  cardPlaceholder: '#d9d9d9',
} as const;

export const HERO_GRADIENT = `
  linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.05) 100%),
  linear-gradient(180deg, rgba(0,0,0,0.16) 6%, rgba(0,0,0,0.03) 38%, rgba(0,0,0,0.04) 62%, rgba(0,0,0,0.18) 94%)
`;

export const FONTS = {
  serif: '"Cormorant Garamond", "Georgia", serif',
  sans: '"Inter", "Helvetica Neue", "Arial", sans-serif',
} as const;

export const RADIUS = {
  sm: 5,
  md: 10,
  lg: 18,
  xl: 42,
  xxl: 64,
} as const;

export const SPACING = {
  sectionPaddingX: { xs: '24px', sm: '40px', md: '71px' },
  sectionPaddingY: { xs: '80px', md: '120px' },
  maxContent: 1373,
  /** Gap between section eyebrow label and headline */
  eyebrowToHeadline: { xs: '8px', md: '14px' },
  sectionViewport: {
    minHeight: { xs: 'auto', md: '100svh' },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
    paddingTop: { xs: '80px', md: '92px' },
    paddingBottom: { xs: '80px', md: '120px' },
  },
  buttonGroupGap: '60px',
} as const;

export const HEADER_HEIGHT = { xs: 60, md: 80 } as const;

/** Section headline font sizes at −10 % (line-height ratios & letter-spacing unchanged) */
export const sectionHeadlineSize = (px: number) => `${+(px * 0.9).toFixed(3)}px`;
const sh = sectionHeadlineSize;
/** Serif accent in mixed Inter headlines — slightly larger for optical cap-height match */
const mixedHeadlineAccentSize = (sansPx: number) => sectionHeadlineSize(sansPx * 1.16);

export const TYPO = {
  navLink: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '16.54px',
    lineHeight: '22.68px',
    letterSpacing: '0.496px',
  },
  eyebrow: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '19px',
    letterSpacing: '2.02px',
    textTransform: 'uppercase' as const,
  },
  sectionHeadlineSerif: {
    fontFamily: FONTS.serif,
    fontWeight: 300,
    fontSize: { xs: sh(36), md: sh(47.25) },
    lineHeight: 1.1,
    textTransform: 'none' as const,
  },
  sectionHeadlineSans: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: { xs: sh(36), md: sh(47.25) },
    lineHeight: 1.1,
    letterSpacing: '-0.48px',
    textTransform: 'none' as const,
  },
  aboutIntro: {
    fontFamily: FONTS.serif,
    fontWeight: 300,
    fontSize: { xs: sh(28), sm: sh(36), md: sh(47.25) },
    fontStyle: 'normal',
    lineHeight: 1.1,
    color: '#393939',
  },
  aboutSide: {
    fontFamily: FONTS.sans,
    fontWeight: 300,
    fontSize: '16.2px',
    lineHeight: '21.6px',
    fontStyle: 'normal',
    color: '#393939',
  },
  /** Opener + Hero: „Kanzlei für Familienrecht“ — nur fontSize bewusst anpassbar */
  kanzleiTagline: {
    fontFamily: FONTS.sans,
    fontSize: { xs: '10px', sm: '12px', md: '13px' },
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '2.126px',
    textTransform: 'uppercase' as const,
    color: '#fff',
  },
  heroHeadline: {
    fontFamily: FONTS.serif,
    fontWeight: 300,
    fontSize: { xs: '34px', sm: '44px', md: '56px', lg: '62px' },
    lineHeight: 1.1,
    letterSpacing: { xs: '-1px', md: '-1.8px' },
    color: '#fff',
  },
  bigHeadline: {
    fontFamily: FONTS.serif,
    fontWeight: 300,
    fontSize: { xs: sh(41.4), sm: sh(57.5), md: sh(80.5) },
    lineHeight: '1.2',
  },
  dekoWord: {
    fontFamily: FONTS.serif,
    fontWeight: 400,
    fontSize: { xs: sh(36), md: sh(50) },
    lineHeight: '22px',
    letterSpacing: '-1px',
    color: COLORS.textMutedOnLight,
    textTransform: 'none' as const,
  },
  methodikHeadlineSans: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: { xs: sh(32), md: sh(47.25) },
    fontStyle: 'normal',
    lineHeight: 1.1,
    letterSpacing: '-0.945px',
    textTransform: 'uppercase' as const,
    color: '#3a3a3a',
  },
  methodikHeadlineAccent: {
    fontFamily: FONTS.serif,
    fontWeight: 400,
    fontSize: { xs: mixedHeadlineAccentSize(32), md: mixedHeadlineAccentSize(47.25) },
    fontStyle: 'normal',
    lineHeight: { xs: 1.2, md: 1.15 },
    letterSpacing: '-1px',
    textTransform: 'uppercase' as const,
    color: '#4f4f4f',
  },
  entwicklungenHeadlineSerif: {
    fontFamily: FONTS.serif,
    fontWeight: 400,
    fontSize: { xs: sh(36), md: sh(49.868) },
    lineHeight: '21.942px',
    letterSpacing: '-0.9974px',
    textTransform: 'uppercase' as const,
    color: '#3a3a3a',
  },
  entwicklungenHeadlineSans: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: { xs: sh(32), md: sh(47.125) },
    lineHeight: 1.1,
    letterSpacing: '-1.4137px',
    textTransform: 'uppercase' as const,
    color: '#393939',
  },
  guillemet: {
    fontFamily: FONTS.serif,
    fontWeight: 300,
    fontSize: { xs: sh(41.4), md: sh(62.1) },
    lineHeight: 0.95,
    letterSpacing: '-1.8px',
    textTransform: 'uppercase' as const,
  },
  teamName: {
    fontFamily: FONTS.serif,
    fontWeight: 300,
    fontSize: { xs: '30px', md: '40px' },
    lineHeight: 1.1,
    color: COLORS.text,
  },
  bodyLight: {
    fontFamily: FONTS.sans,
    fontWeight: 300,
    fontSize: '15px',
    lineHeight: '22px',
    letterSpacing: '-0.15px',
    color: COLORS.text,
  },
  bodyOnDark: {
    fontFamily: FONTS.sans,
    fontWeight: 300,
    fontSize: '15px',
    lineHeight: '22px',
    color: '#fff',
  },
  bodyLargeLight: {
    fontFamily: FONTS.sans,
    fontWeight: 300,
    fontSize: '15.958px',
    lineHeight: '21.94px',
    color: COLORS.textMuted,
  },
  buttonLight: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.5,
    letterSpacing: '0.42px',
    color: COLORS.text,
  },
  buttonDark: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: 'normal',
    letterSpacing: '0.32px',
    color: '#fff',
  },
  footerLink: {
    fontFamily: FONTS.sans,
    fontWeight: 300,
    fontSize: '14.7px',
    lineHeight: '14.7px',
    letterSpacing: '2.205px',
    textTransform: 'uppercase' as const,
    color: '#fff',
  },
  footerDetail: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.6,
    color: COLORS.text,
  },
  footerName: {
    fontFamily: FONTS.sans,
    fontWeight: 300,
    fontSize: '14px',
    lineHeight: 1.6,
    color: COLORS.textMuted,
  },
  footerAddress: {
    fontFamily: FONTS.sans,
    fontWeight: 300,
    fontSize: '14px',
    lineHeight: 1.6,
    color: COLORS.textMuted,
  },
  footerContactLine: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#f2f2f2',
  },
  inputField: {
    fontFamily: FONTS.sans,
    fontWeight: 300,
    fontSize: '14.28px',
    lineHeight: 'normal',
    color: COLORS.inputPlaceholder,
  },
  leistungTitle: {
    fontFamily: FONTS.serif,
    fontWeight: 300,
    fontStyle: 'normal',
    fontSize: { xs: '22px', sm: '28px', md: '35px' },
    lineHeight: 1.1,
    color: '#3a3a3a',
  },
} as const;

/** Top-Bar: Menü + Kontakt (−10 %) */
export const navLinkTopBarSx = {
  fontFamily: FONTS.sans,
  fontWeight: 400,
  fontSize: 'calc(16.54px * 0.9)',
  lineHeight: 'calc(22.68px * 0.9)',
  letterSpacing: 'calc(0.496px * 0.9)',
} as const;

const buttonBase = {
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '15px 25px',
  borderRadius: 0,
  textTransform: 'none' as const,
  whiteSpace: 'nowrap' as const,
  boxShadow: 'none',
  backgroundColor: 'transparent',
  width: 150,
  minWidth: 150,
  boxSizing: 'border-box' as const,
};

export const BUTTON = {
  light: {
    ...buttonBase,
    border: `1px solid ${COLORS.btnBorderLight}`,
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.5,
    letterSpacing: '0.42px',
    color: COLORS.text,
    transition:
      'border-color 0.35s ease-out, background 0.35s ease-out, color 0.15s ease-out',
    '&:hover': {
      border: `1px solid ${COLORS.bg}`,
      background: `linear-gradient(180deg, ${COLORS.bg} 0%, ${COLORS.bg} 100%)`,
      color: COLORS.textOnDark,
      boxShadow: 'none',
    },
  },
  dark: {
    ...buttonBase,
    border: `1px solid ${COLORS.btnBorderDark}`,
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: 'normal',
    letterSpacing: '0.32px',
    color: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      boxShadow: 'none',
    },
  },
} as const;
